import React, { useCallback, useEffect, useMemo } from "react";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
// import { useButtonShadow } from "../../../../utils/buttonShadow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { decrementBet, closeMenu, closeSymbolPopup } from "../../../../State/actions";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import {
  selectIsSpinning,
  selectUserInterfaceDisabled,
  selectSessionExpired,
  selectBet,
  selectBets,
  selectAutoPlayActive,
  selectAnyFeatureTriggeredOrActive,
  selectCriticalAnimationPlaying,
  selectFreeRoundsActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";

const ButtonDecrementBet = ({ x = 0, y = 0 }) => {
  const dispatch = useDispatch();
  const { getResponsiveConfig, device } = useDimensions();

  const isSpinning = useSelector(selectIsSpinning);
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const sessionExpired = useSelector(selectSessionExpired);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const anyFeatureActive = useSelector(selectAnyFeatureTriggeredOrActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);


  const texture = getNewUITexture("icon_minus");

  // Memoize calculations
  const currentBetIndex = useMemo(() => bets.indexOf(bet), [bets, bet]);

  const canDecreaseBet = useMemo(
    () =>
      currentBetIndex > 0 &&
      !isSpinning &&
      !sessionExpired &&
      !autoPlayActive &&
      !userInterfaceDisabled &&
      !anyFeatureActive &&
      !criticalAnimationPlaying &&
      !freeRoundsActive &&
      !replaySessionActive,
    [
      currentBetIndex,
      isSpinning,
      sessionExpired,
      autoPlayActive,
      userInterfaceDisabled,
      anyFeatureActive,
      criticalAnimationPlaying,
      freeRoundsActive,
      replaySessionActive,
    ]
  );

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters, alpha, cursor } =
    useButtonBrightness({ disabled: !canDecreaseBet });

  // Get responsive configuration from context - memoized
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Memoize button radius calculation
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  // Its soft drop shadow under the round texture — utils/buttonShadow, the
  // one source for every round button (2026-10-05).
  // const shadowTexture = useButtonShadow(buttonRadius); // ButtonShadow now

  // Memoize hitArea calculation
  const hitArea = useMemo(
    () => new PIXI.Circle(0, 0, buttonRadius),
    [buttonRadius]
  );

  const handleClick = useCallback(() => {
    // Close menu if open
    dispatch(closeMenu());
    dispatch(closeSymbolPopup());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    if (canDecreaseBet) {
      soundManager.play(SOUND_NAME.button_click);

      // Add press animation before decrementing bet
      triggerPress({
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height
        });

      dispatch(decrementBet());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDecreaseBet, dispatch, device, buttonConfig.width, buttonConfig.height]);

  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  // Memoize draw function for Graphics component
  const drawGraphics = useCallback(
    (g) => {
      g.clear();
      // Nearly invisible fill (required for hit detection to work)
      g.beginFill(0x000000, 0.01);
      g.drawCircle(0, 0, buttonRadius);
      g.endFill();
    },
    [buttonRadius]
  );

  return (
    <Container x={x + buttonConfig.width / 2} y={y}>
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="decrement_bet_button_interactive_area"
        eventMode="static"
        cursor={cursor}
        hitArea={hitArea}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={drawGraphics}
      />

      {/* Its soft drop shadow, under the round texture (utils/buttonShadow) */}
      {/* {shadowTexture && ( */}
      {/* <Sprite */}
      {/* texture={shadowTexture} */}
      {/* name="decrement_bet_button_shadow" */}
      {/* anchor={0.5} */}
      {/* x={0} */}
      {/* y={0} */}
      {/* eventMode="none" */}
      {/* alpha={alpha} */}
      {/* /> */}
      {/* )} */}
      <ButtonShadow target={buttonRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="secondary_button_decrement_bet"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        roundPixels={true}
        x={0}
        y={0}
        eventMode="none"
        alpha={alpha}
      />
    </Container>
  );
};

export default ButtonDecrementBet;
